import { ChangeEvent, useState } from 'react';
import { useNavigate } from 'react-router';
import FileSaver from 'file-saver';
import { Box, Flex, Heading, Text, Icon, Button, useToast, Select } from '@chakra-ui/react';

import PageHeader from 'layout/pageHeader';
import MDropZone from 'components/molecules/MDropZone';
import icons from 'shared/icons';
import { useLazyExportClientProductsQuery, useUploadClientProductsMutation } from 'core/API/productsApi';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { EXCEL_FILE_TYPE } from 'shared/const';
import { linkStyle } from 'shared/styles';
import { TRetailerProp } from 'shared/types';
import { useTranslation } from 'helpers/languages/translations';

import ARetLangSelector from '../../../components/atoms/ARetLangSelector';
import { IErrorUploadResponse } from '../types';
import DescriptionHtml from 'components/molecules/DescriptionHtml';

const TitleSplitUpload = () => {
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [retailer, setRetailer] = useState<TRetailerProp>('default');
    const [lang, setLang] = useState<string>('default');
    const [uploadMethod, setUploadMethod] = useState<'keep' | 'override'>('keep');

    const toast = useToast();
    const navigate = useNavigate();
    const translate = useTranslation();
    const [exportTitleSplits] = useLazyExportClientProductsQuery();
    const [uploadTitleSplits, { isLoading }] = useUploadClientProductsMutation();

    const handleChangeFile = (file: File | null) => {
        if (file?.type === EXCEL_FILE_TYPE) {
            setUploadedFile(file);
        } else {
            if (file !== null)
                toast({
                    title: translate('error.upload.file-invalid-type.xlsx'),
                    status: 'error',
                    isClosable: true,
                });
            setUploadedFile(null);
        }
    };

    const goBack = () => navigate(-1);
    const getTemplate = async () => {
        try {
            const { data: res, isError } = await exportTitleSplits({
                isTemplate: true,
                isTitleSplit: true,
                lang: 'default',
                retailer: 'default',
            });
            if (isError || !res) throw new Error(translate('splits.upload.templateError') as string);

            const blob = new Blob([res], {
                type: EXCEL_FILE_TYPE,
            });

            FileSaver.saveAs(blob, translate('splits.upload.templateFileName') as string);
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const uploadTitleSplitFromExcel = async () => {
        try {
            const bodyFormData = new FormData();
            bodyFormData.append('excelFile', uploadedFile as File);
            bodyFormData.append('isTitleSplit', 'true');
            bodyFormData.append('retailer', retailer === 'default' ? 'default' : `${retailer.id}`);
            bodyFormData.append('lang', lang);
            bodyFormData.append('uploadMethod', uploadMethod);

            const res = await uploadTitleSplits(bodyFormData);
            setUploadedFile(null);

            if ('error' in res) {
                const error = res.error as IErrorUploadResponse;
                if (error?.data?.missing_headers && error?.data?.missing_headers?.length) {
                    throw new Error(
                        translate('product.upload.error.missing_specific_headers', {
                            headers: error?.data?.missing_headers.join(', '),
                        }) as string,
                    );
                }
                if (error?.data?.invalid_split_headers && error?.data?.invalid_split_headers?.length) {
                    throw new Error(
                        translate('product.upload.error.invalid_split_headers', {
                            headers: error?.data?.invalid_split_headers.join(', '),
                        }) as string,
                    );
                }

                throw new Error(translate('splits.upload.error') as string);
            }

            goBack();
            toast({
                title: translate('splits.upload.success'),
                status: 'success',
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const renderDescriptionSection = () => {
        const UploadIcon = icons.upload;
        const handleChangeUploadMethod = (e: ChangeEvent<HTMLSelectElement>) => {
            setUploadMethod(e.target.value as 'keep' | 'override');
        };

        return (
            <Flex w="40%" justifyContent="flex-start" direction="column" textAlign="start" mr={5}>
                <Text>
                    {translate('product.upload.recommendation', {
                        template: (
                            <Text
                                onClick={intentionalyFloatingPromiseReturn(getTemplate)}
                                as="span"
                                {...linkStyle}
                                mx={1}
                            >
                                {translate('product.upload.downloadTemplate')}
                            </Text>
                        ),
                    })}
                </Text>
                <Text mt={2}>{translate('splits.upload.section1')} </Text>
                <Text my={2}>{translate('splits.upload.section2')}</Text>
                <Text> {translate('product.upload.retailerSelect')}</Text>
                <Box my={4}>
                    <ARetLangSelector
                        selectedRetailer={retailer}
                        selectedLang={lang}
                        setSelectedLang={setLang}
                        setSelectedRetailer={setRetailer}
                    />
                </Box>
                <Select mb={8} mt={2} bg="white" value={uploadMethod} onChange={handleChangeUploadMethod}>
                    <option value="keep">{translate('splits.upload.method.keep')} </option>
                    <option value="override"> {translate('splits.upload.method.override')}</option>
                </Select>
                <MDropZone chosenFile={uploadedFile} handleChangeFile={handleChangeFile} />
                <Flex mt="24px" justifyContent="space-between">
                    <Button
                        onClick={intentionalyFloatingPromiseReturn(uploadTitleSplitFromExcel)}
                        isLoading={isLoading}
                        isDisabled={!uploadedFile}
                        rightIcon={<Icon as={UploadIcon} />}
                        colorScheme="blue"
                        bg="blueMain"
                    >
                        {translate('product.upload.apply')}
                    </Button>
                </Flex>
            </Flex>
        );
    };

    return (
        <Box>
            <PageHeader
                pageTitle={translate('splits.upload.header') as string}
                icon={{ name: 'arrowBack', props: { onClick: goBack } }}
            />
            <Box px="32px">
                <Flex justifyContent="start">
                    <Heading as="h2" fontWeight="semibold" fontSize="18px" mb="18px">
                        {translate('product.upload.uploadFile')}
                    </Heading>
                </Flex>
                <Flex direction="row">
                    {renderDescriptionSection()}
                    <DescriptionHtml h="fit-content" tradKey="splits.upload.description" />
                </Flex>
            </Box>
        </Box>
    );
};

export default TitleSplitUpload;
